import type { LucideProps } from "lucide-react";
import {
  AlignLeft,
  Atom,
  BicepsFlexed,
  Blocks,
  BookOpen,
  BookOpenCheck,
  Bot,
  Brain,
  Briefcase,
  Building2,
  Calculator,
  CalendarDays,
  Car,
  Circle,
  CircleDot,
  ClipboardList,
  Code,
  Construction,
  Diamond,
  Dna,
  Dribbble,
  Droplet,
  Dumbbell,
  Eye,
  FileText,
  Flag,
  FlaskConical,
  Footprints,
  Gamepad2,
  Goal,
  GraduationCap,
  Grid2x2,
  Grid3x3,
  HeartHandshake,
  HeartPulse,
  History,
  Home,
  Inbox,
  LandPlot,
  Landmark,
  Languages,
  LayoutDashboard,
  Leaf,
  Library,
  ListChecks,
  Map,
  Medal,
  Megaphone,
  MessageSquare,
  Microscope,
  Monitor,
  Music,
  PieChart,
  Puzzle,
  Scale,
  School,
  Shield,
  ShieldCheck,
  Sigma,
  Sparkles,
  Stethoscope,
  ToggleLeft,
  Trophy,
  Type,
  UserCircle,
  UserCog,
  Users,
  Volleyball,
  Youtube,
  Zap,
} from "lucide-react";

/**
 * Iconos por nombre, para los que vienen como TEXTO (config de navegación,
 * `icon` de los módulos de contenido, heroes).
 *
 * ⚠️ EL MAPA ES EXPLÍCITO A PROPÓSITO. Antes esto era `import { icons } from
 * "lucide-react"`, que resuelve cualquier nombre en una línea — y arrastra los
 * ~1.500 iconos del paquete al bundle, porque un objeto indexado dinámicamente
 * no se puede tree-shakear. Eran cientos de KB que descargaba cada visita para
 * usar 46.
 *
 * El coste es que un nombre nuevo hay que añadirlo aquí. Es un coste real y
 * pequeño; la alternativa lo paga cada usuario en cada carga.
 *
 * Ese coste se dejó de pagar y no se notó: NUEVE nombres se usaban sin estar en
 * el mapa —AlignLeft, Eye, Flag, Grid2x2, Grid3x3, ListChecks, Monitor, Scale,
 * Type— y salían como un círculo vacío. Siete de ellos a la vez en STEM y
 * Juegos: Trivia, ¿Verdadero o Falso?, Memoria, Ahorcado, Sopa de Letras,
 * Ordena la Oración y DigitalLearn.
 *
 * Ya no hace falta acordarse: `web/test/iconos.mapa.test.ts` compara este mapa
 * con los nombres que de verdad se usan y falla si se descuelga alguno. El
 * grep que había aquí antes miraba solo `web/src` — y los módulos de contenido
 * traen su `icon` desde JSON, así que ni con eso se habría visto entero.
 */
const ICONOS: Record<string, React.ComponentType<LucideProps>> = {
  AlignLeft,
  Atom,
  BicepsFlexed,
  Blocks,
  BookOpen,
  BookOpenCheck,
  Bot,
  Brain,
  Briefcase,
  Building2,
  Calculator,
  CalendarDays,
  Car,
  Circle,
  CircleDot,
  ClipboardList,
  Code,
  Construction,
  Diamond,
  Dna,
  Dribbble,
  Droplet,
  Dumbbell,
  Eye,
  FileText,
  Flag,
  FlaskConical,
  Footprints,
  Gamepad2,
  Goal,
  GraduationCap,
  Grid2x2,
  Grid3x3,
  HeartHandshake,
  HeartPulse,
  History,
  Home,
  Inbox,
  LandPlot,
  Landmark,
  Languages,
  LayoutDashboard,
  Leaf,
  Library,
  ListChecks,
  Map,
  Medal,
  Megaphone,
  MessageSquare,
  Microscope,
  Monitor,
  Music,
  PieChart,
  Puzzle,
  Scale,
  School,
  Shield,
  ShieldCheck,
  Sigma,
  Sparkles,
  Stethoscope,
  ToggleLeft,
  Trophy,
  Type,
  UserCircle,
  UserCog,
  Users,
  Volleyball,
  Youtube,
  Zap,
};

/**
 * Los nombres que el mapa sabe resolver. Se exporta SOLO para que la prueba
 * pueda comparar esta lista con los nombres que de verdad se usan — ver
 * web/test/iconos.mapa.test.ts. Nueve nombres llevaban tiempo fuera y salían
 * como un círculo vacío sin que nada avisara.
 */
export const NOMBRES_DE_ICONO: readonly string[] = Object.keys(ICONOS);

interface Props extends LucideProps {
  name: string;
}

export function Icon({ name, ...props }: Props) {
  // Un nombre desconocido cae en Circle en vez de romper la pantalla: el icono es
  // decoración, y una sala entera no debe caerse porque falte una entrada aquí.
  const Cmp = ICONOS[name] ?? Circle;
  // Oculto al lector de pantalla por defecto: siempre va junto a un texto que
  // ya dice lo mismo (el nombre de la sala, de la materia). Quien lo use solo,
  // sin texto, puede pasar `aria-hidden={false}` y un `aria-label`.
  return <Cmp aria-hidden {...props} />;
}
